 
/**
 * S22 · Reviewer inbox — submitted sessions on this device, awaiting approval.
 *
 * Both gaps this docstring used to list are closed.
 * `/sessions/review-queue` is a pulled resource, so submissions from other
 * devices arrive; and a decision leaves on `session.upsert`, which carries
 * `status` and always did — the old bug was the wrong op, not a missing one.
 *
 * Three outcomes, and the middle one is why the screen is worth having.
 * Approving ends a session and rejecting ends it too; `changes_requested`
 * hands it back with a reason the inspector can act on. That reason has two
 * halves: a note saying what is wrong, and optionally the fields it is about,
 * which the form marks and can jump to.
 *
 * A note is required to send something back. "This came back" with no reason
 * sends the inspector to find the reviewer rather than the defect. Naming
 * fields is not required — plenty of send-backs are about a photograph, an
 * access problem, or the whole submission.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { SessionSummary } from '@kapture/types';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import {
  useDecideSessionMutation,
  useInboxSessions,
  useSession,
} from '@kapture/client-core';
import { getTemplate } from '@kapture/domain';

export const Route = createFileRoute('/inbox')({
  component: InboxPage,
  staticData: { mobile: true },
});

function InboxPage() {
  const { data: sessions, isLoading } = useInboxSessions();
  const list = sessions ?? [];

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/"
          aria-label="Back"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Reviewer inbox</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {list.length} submitted · awaiting approval
          </div>
        </div>
        <Chip tone="brandSoft" size="sm">
          Reviewer · Sarah
        </Chip>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {isLoading && (
          <div style={{ padding: 32, textAlign: 'center', color: T.textMute, fontSize: 13 }}>
            Loading inbox…
          </div>
        )}
        {!isLoading && list.length === 0 && <EmptyInbox />}
        {list.map((s) => (
          <InboxRow key={s.id} session={s} />
        ))}
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function InboxRow({ session }: { session: SessionSummary }) {
  const decide = useDecideSessionMutation();
  const { data: full } = useSession(session.id);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [flagged, setFlagged] = useState<string[]>([]);

  const template = getTemplate(full?.templateId);
  const fields = template?.fields ?? [];

  function toggle(id: string) {
    setFlagged((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );
  }

  return (
    <div
      style={{
        padding: 14,
        background: T.surface,
        border: `1px solid ${T.stroke}`,
        borderRadius: 14,
        marginBottom: 10,
        boxShadow: T.sh,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 19,
            background: T.brand,
            color: T.brandInk,
            display: 'grid',
            placeItems: 'center',
            fontFamily: T.font,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          SC
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              fontFamily: T.font,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {session.title}
          </div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
            Submitted {new Date(session.updatedAt).toLocaleDateString()}
            {' · '}
            {session.captureCount} capture{session.captureCount === 1 ? '' : 's'}
          </div>
        </div>
        <Chip tone="brandSoft" size="sm">
          {session.status}
        </Chip>
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 10,
          background: T.ink,
          color: '#fff',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Ico d={I.sparkle} size={14} fill={T.brand} stroke={T.brand} />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              fontFamily: T.mono,
              color: 'rgba(255,255,255,0.55)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 4,
            }}
          >
            AI brief
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {session.captureCount} field events attached. Priority findings auto-surfaced
            for review; sync queue clean.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId: session.id }}
          style={{
            padding: '8px 14px',
            borderRadius: 10,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            color: T.text,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 13,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Ico d={I.folder} size={13} />
          Open timeline
        </Link>
        <div style={{ flex: 1 }} />
        {!asking && (
          <>
            <Btn
              tone="light"
              size="md"
              disabled={decide.isPending}
              onClick={() => setAsking(true)}
            >
              Ask for a fix
            </Btn>
            <Btn
              tone="good"
              size="md"
              iconRight={<Ico d={I.check2} size={15} sw={2.4} />}
              disabled={decide.isPending}
              onClick={() =>
                void decide.mutateAsync({ sessionId: session.id, decision: 'approved' })
              }
              style={{ opacity: decide.isPending ? 0.5 : 1 }}
            >
              {decide.isPending ? 'Saving…' : 'Approve'}
            </Btn>
          </>
        )}
      </div>

      {asking && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What needs fixing?"
            aria-label="Review note"
            style={{
              width: '100%',
              minHeight: 72,
              padding: 10,
              borderRadius: 10,
              border: `1px solid ${T.stroke}`,
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              resize: 'vertical',
              outline: 'none',
            }}
          />

          {fields.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 10,
                  fontFamily: T.mono,
                  color: T.textMute,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Fields this is about — optional
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {fields.map((f) => {
                  const on = flagged.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      onClick={() => toggle(f.id)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 100,
                        border: `1px solid ${on ? T.warn : T.stroke}`,
                        background: on ? T.warnSoft : T.surface,
                        color: on ? T.warn : T.textDim,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: T.font,
                        cursor: 'pointer',
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn tone="light" size="md" onClick={() => setAsking(false)}>
              Cancel
            </Btn>
            <Btn
              tone="primary"
              size="md"
              full
              // A reason is required; naming fields is not.
              disabled={note.trim().length === 0 || decide.isPending}
              onClick={() =>
                void decide
                  .mutateAsync({
                    sessionId: session.id,
                    decision: 'changes_requested',
                    note: note.trim(),
                    flaggedFields: flagged,
                  })
                  .then(() => setAsking(false))
              }
            >
              Send back
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyInbox() {
  return (
    <div
      style={{
        padding: '40px 12px',
        textAlign: 'center',
        color: T.textDim,
        fontFamily: T.font,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 16,
          background: T.surface2,
          color: T.textDim,
          margin: '0 auto 16px',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Ico d={I.bell} size={28} sw={1.6} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
        Nothing to review
      </div>
      All submitted sessions are approved. New ones land here the moment they're signed.
    </div>
  );
}
